import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { theme, alpha } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import AuthModal from './AuthModal';

// Links reales a las tiendas (app publicada iOS + Android).
const APP_STORE_URL = 'https://apps.apple.com/ec/app/futbolero/id6758485409';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.futbolero.app';

const AppleGlyph: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.42 2.2-1.13 3-.79.9-2.08 1.6-3.15 1.51-.13-1.09.42-2.24 1.09-2.96.75-.81 2.08-1.42 3.19-1.55zM20.9 17.02c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.39 3.53-4.12 3.54-1.54.01-1.93-1-4.02-1-2.09.01-2.52 1.02-4.06 1.01-1.73-.02-3.06-1.78-4.05-3.35C1.13 16.98.8 12.6 2.4 10.25c.98-1.44 2.53-2.29 3.99-2.29 1.49 0 2.42 1.01 3.66 1.01 1.2 0 1.93-1.01 3.66-1.01 1.31 0 2.7.71 3.69 1.94-3.24 1.77-2.71 6.39.5 7.12z" />
  </svg>
);

const PlayGlyph: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3.6 2.3 13.5 12 3.6 21.7c-.35-.2-.6-.6-.6-1.1V3.4c0-.5.25-.9.6-1.1z" fill="#00E676" />
    <path d="M16.9 8.6 13.5 12l3.4 3.4 3.9-2.24c.9-.52.9-1.8 0-2.32L16.9 8.6z" fill="#FFC107" />
    <path d="M3.6 2.3c.28-.16.62-.18.96.02L16.9 8.6 13.5 12 3.6 2.3z" fill="#00E5FF" />
    <path d="M3.6 21.7 13.5 12l3.4 3.4-12.34 6.28c-.34.2-.68.18-.96.02z" fill="#FF3D57" />
  </svg>
);

const StoreBadge: React.FC<{
  href: string;
  glyph: React.ReactNode;
  top: string;
  bottom: string;
}> = ({ href, glyph, top, bottom }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      background: '#000',
      border: '1px solid #333',
      borderRadius: '12px',
      padding: '10px 18px',
      textDecoration: 'none',
      minWidth: '170px',
    }}
  >
    {glyph}
    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
      <span style={{ fontSize: '10px', color: '#c8c8c8', letterSpacing: '0.5px' }}>{top}</span>
      <span style={{ fontSize: '17px', color: '#fff', fontWeight: 600 }}>{bottom}</span>
    </span>
  </a>
);

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const primary = theme.colors.primary;

  // Punto de entrada: si ya hay sesión, a la app; si no, abrir login.
  const handleStart = () => (user ? navigate('/home') : setShowLoginModal(true));

  const features = [
    { icon: '⚖️', title: 'Equipos parejos', text: 'Armamos los equipos equilibrados por nivel en un toque.' },
    { icon: '📲', title: 'Invita por WhatsApp', text: 'Tus amigos confirman desde un link, sin descargar la app.' },
    { icon: '🏆', title: 'Stats y reseñas', text: 'Goles, asistencias, MVP y reseñas después de cada partido.' },
    { icon: '📍', title: 'Partidos públicos', text: 'Encuentra partidos abiertos cerca tuyo y súmate.' },
  ];

  const steps = [
    { title: 'Crea el partido', text: 'Elige cancha, día y hora. Listo en menos de un minuto.' },
    { title: 'Invita a tu grupo', text: 'Por WhatsApp o desde la app. Tus amigos confirman al toque.' },
    { title: 'Juega y carga las stats', text: 'Equipos parejos, y al final goles, MVP y reseñas.' },
  ];

  return (
    <div
      className="fb-landing"
      style={{
        background: theme.colors.background,
        minHeight: '100vh',
        color: theme.colors.text,
        fontFamily: theme.fonts.body,
      }}
    >
      {/* Los headings globales del sitio vienen en un gris oscuro que no contrasta
          contra el fondo oscuro de la landing; forzamos el color de texto claro. */}
      <style>{`.fb-landing h1, .fb-landing h2, .fb-landing h3 { color: ${theme.colors.text} !important; }`}</style>
      {/* ─── Header ─── */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '14px 20px' : '16px 48px',
          background: alpha('background', 0.85),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.colors.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '34px',
              height: '34px',
              background: primary,
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'rotate(-5deg)',
            }}
          >
            <span style={{ fontSize: '18px' }}>⚽</span>
          </div>
          <span style={{ fontSize: '20px', fontWeight: 700, color: primary, letterSpacing: '0.5px' }}>
            FUTBOLERO
          </span>
        </div>
        <button
          onClick={handleStart}
          style={{
            background: 'transparent',
            color: theme.colors.text,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '10px',
            padding: '9px 18px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: theme.fonts.body,
          }}
        >
          Iniciar sesión
        </button>
      </header>

      {/* ─── Hero ─── */}
      <section
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '40px' : '64px',
          maxWidth: '1160px',
          margin: '0 auto',
          padding: isMobile ? '48px 24px 8px' : '80px 48px 40px',
        }}
      >
        {/* Copy */}
        <div style={{ flex: 1, textAlign: isMobile ? 'center' : 'left', maxWidth: '560px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: alpha('primary', 0.13),
              color: primary,
              borderRadius: '999px',
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '20px',
            }}
          >
            ⚽ Fútbol amateur, organizado
          </div>
          <h1
            style={{
              fontSize: isMobile ? '2.2rem' : '3.2rem',
              fontWeight: 700,
              lineHeight: 1.1,
              margin: '0 0 18px',
              letterSpacing: '-0.02em',
            }}
          >
            Organiza tus partidos{' '}
            <span style={{ color: primary }}>sin caos</span>
          </h1>
          <p
            style={{
              fontSize: isMobile ? '1.05rem' : '1.25rem',
              color: theme.colors.textSecondary,
              lineHeight: 1.6,
              margin: '0 0 32px',
            }}
          >
            Arma el partido, invita a tu grupo, equipos parejos y las stats al final.
            Todo en un solo lugar — en tu celular o en la web.
          </p>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              justifyContent: isMobile ? 'center' : 'flex-start',
              marginBottom: '20px',
            }}
          >
            <button
              onClick={handleStart}
              style={{
                background: primary,
                color: '#08130c',
                border: 'none',
                borderRadius: '12px',
                padding: '15px 30px',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: theme.fonts.body,
              }}
            >
              Empieza gratis
            </button>
            <a
              href="#descargar"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'transparent',
                color: theme.colors.text,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '12px',
                padding: '15px 28px',
                fontSize: '16px',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Descargar la app
            </a>
          </div>
          <p style={{ fontSize: '13px', color: theme.colors.textSecondary, margin: 0 }}>
            Disponible en iOS, Android y navegador.
          </p>
        </div>

        {/* Phone mockup */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              width: '260px',
              background: '#0e0f10',
              border: '8px solid #1c1e20',
              borderRadius: '38px',
              padding: '14px 12px 18px',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '12px',
                color: primary,
                fontWeight: 700,
                fontSize: '13px',
                letterSpacing: '1px',
              }}
            >
              FUTBOLERO
            </div>
            {/* match card */}
            <div style={{ background: '#1a1c1e', borderRadius: '16px', padding: '14px', marginBottom: '10px' }}>
              <div style={{ fontSize: '11px', color: '#8a8f93', marginBottom: '10px' }}>
                Martes 20:00 · La Bombonerita
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#10B981', margin: '0 auto 6px' }} />
                  <div style={{ fontSize: '12px', color: '#fff', fontWeight: 600 }}>Claros</div>
                </div>
                <div style={{ fontSize: '13px', color: '#8a8f93', fontWeight: 700 }}>VS</div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#2196F3', margin: '0 auto 6px' }} />
                  <div style={{ fontSize: '12px', color: '#fff', fontWeight: 600 }}>Oscuros</div>
                </div>
              </div>
            </div>
            <div style={{ background: '#1a1c1e', borderRadius: '12px', padding: '10px 12px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: '#fff', fontWeight: 700 }}>8/10</span>
                <span style={{ fontSize: '11px', color: '#8a8f93' }}>Faltan 2</span>
              </div>
              <div style={{ height: 6, background: '#0e0f10', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: '80%', height: '100%', background: primary }} />
              </div>
            </div>
            <div
              style={{
                background: primary,
                color: '#08130c',
                borderRadius: '12px',
                padding: '11px',
                textAlign: 'center',
                fontSize: '13px',
                fontWeight: 700,
              }}
            >
              Me sumo al partido
            </div>
          </div>
        </div>
      </section>

      {/* ─── Prueba social LatAm (países reales top de uso) ─── */}
      <div style={{ textAlign: 'center', padding: isMobile ? '0 24px 24px' : '0 48px 28px' }}>
        <p style={{ color: theme.colors.textSecondary, fontSize: '13px', margin: 0, letterSpacing: '0.3px' }}>
          Ya se juega en Colombia · México · Argentina · Ecuador · Perú y toda LatAm
        </p>
      </div>

      {/* ─── Cómo funciona ─── */}
      <section
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: isMobile ? '20px 24px 8px' : '32px 48px 8px',
          textAlign: 'center',
        }}
      >
        <h2 style={{ fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 700, margin: '0 0 8px' }}>
          Así de fácil
        </h2>
        <p style={{ color: theme.colors.textSecondary, fontSize: '1.05rem', margin: '0 0 28px' }}>
          Del "¿jugamos?" a la cancha en 3 pasos.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px',
            textAlign: 'left',
          }}
        >
          {steps.map((s, i) => (
            <div
              key={s.title}
              style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '16px',
                padding: '22px 20px',
              }}
            >
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  background: primary,
                  color: '#08130c',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  marginBottom: '12px',
                }}
              >
                {i + 1}
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 6px' }}>{s.title}</h3>
              <p style={{ fontSize: '14px', color: theme.colors.textSecondary, margin: 0, lineHeight: 1.5 }}>
                {s.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Descargar / plataformas ─── */}
      <section
        id="descargar"
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: isMobile ? '40px 24px' : '56px 48px',
          textAlign: 'center',
        }}
      >
        <h2 style={{ fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 700, margin: '0 0 10px' }}>
          Llévalo a donde juegues
        </h2>
        <p style={{ color: theme.colors.textSecondary, fontSize: '1.05rem', margin: '0 0 28px' }}>
          Descarga la app o entra desde el navegador. Tu cuenta es la misma en todos lados.
        </p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '14px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <StoreBadge href={APP_STORE_URL} glyph={<AppleGlyph />} top="Descárgalo en" bottom="App Store" />
          <StoreBadge href={PLAY_STORE_URL} glyph={<PlayGlyph />} top="Disponible en" bottom="Google Play" />
          <button
            onClick={handleStart}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: alpha('primary', 0.13),
              color: primary,
              border: `1px solid ${alpha('primary', 0.4)}`,
              borderRadius: '12px',
              padding: '13px 22px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: theme.fonts.body,
              minWidth: '170px',
              justifyContent: 'center',
            }}
          >
            🌐 Usar en el navegador
          </button>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section
        style={{
          maxWidth: '1100px',
          margin: '0 auto',
          padding: isMobile ? '16px 24px 48px' : '24px 48px 72px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '16px',
          }}
        >
          {features.map((f) => (
            <div
              key={f.title}
              style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '16px',
                padding: '22px 24px',
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start',
              }}
            >
              <div
                style={{
                  fontSize: '24px',
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: alpha('primary', 0.13),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </div>
              <div>
                <h3 style={{ fontSize: '17px', fontWeight: 700, margin: '0 0 6px' }}>{f.title}</h3>
                <p style={{ fontSize: '14px', color: theme.colors.textSecondary, margin: 0, lineHeight: 1.55 }}>
                  {f.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CTA final ─── */}
      <section
        style={{
          maxWidth: '760px',
          margin: '0 auto',
          padding: isMobile ? '8px 24px 56px' : '16px 48px 88px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            background: alpha('primary', 0.1),
            border: `1px solid ${alpha('primary', 0.35)}`,
            borderRadius: '24px',
            padding: isMobile ? '36px 24px' : '48px 40px',
          }}
        >
          <h2 style={{ fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 700, margin: '0 0 12px' }}>
            Arma tu primer partido hoy
          </h2>
          <p style={{ color: theme.colors.textSecondary, fontSize: '1.05rem', margin: '0 0 26px' }}>
            Gratis, en menos de un minuto.
          </p>
          <button
            onClick={handleStart}
            style={{
              background: primary,
              color: '#08130c',
              border: 'none',
              borderRadius: '12px',
              padding: '15px 34px',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: theme.fonts.body,
            }}
          >
            Empieza gratis
          </button>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer
        style={{
          borderTop: `1px solid ${theme.colors.border}`,
          padding: isMobile ? '28px 24px' : '32px 48px',
        }}
      >
        <div
          style={{
            maxWidth: '1100px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: '18px',
          }}
        >
          <span style={{ fontSize: '15px', fontWeight: 700, color: primary }}>FUTBOLERO</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '14px' }}>
            <a href="/faq" style={{ color: theme.colors.textSecondary, textDecoration: 'none' }}>Preguntas frecuentes</a>
            <a href="/contacto" style={{ color: theme.colors.textSecondary, textDecoration: 'none' }}>Contacto</a>
            <a href="/privacy" style={{ color: theme.colors.textSecondary, textDecoration: 'none' }}>Privacidad</a>
            <a href="/canchas" style={{ color: theme.colors.textSecondary, textDecoration: 'none' }}>¿Tienes una cancha?</a>
          </div>
        </div>
      </footer>

      <AuthModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => navigate('/home')}
      />
    </div>
  );
};

export default LandingPage;
